import { useCallback } from 'react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import Container from '@ui/container/container';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import { selectProjectCategories } from '@/store/slices/project/selectors';
import { selectAllProjectCategoriesForChannel } from '@/store/slices/categories/selectors';
import { saveWorkingProjectByFields, saveWorkingProjectSessionByField } from '@/store/slices/project';
import { setAllProjectCategoriesInStoreOnly } from '@/store/slices/categories';
import ProjectCategoriesSelect from './project-categories-select/project-categories-select';
import { GENERAL_EDITABLE_PERMISSIONS } from '@/types/permissions.types';
import useHasPermission from '@/utils/authentication/use-has-permission';

import styles from './edit-project-category.module.css';

type TCategoryOption = TReactSelectOption<string, string, string>;

type TEditProjectCategoryProps = {
  /**  passing a session uuid into syncSession will force this component to also update the session tracks */
  syncSessionUuid?: string;
};

const EditProjectCategory = ({ syncSessionUuid }: TEditProjectCategoryProps) => {
  const allProjectCategoriesForChannel = useAppSelector(selectAllProjectCategoriesForChannel);
  const projectCategories = useAppSelector(selectProjectCategories);
  const { hasPermission: hasPermissions } = useHasPermission({
    allowedRoles: GENERAL_EDITABLE_PERMISSIONS,
  });

  const dispatch = useAppDispatch();

  const getDefaultProjectCategories = useCallback(() => {
    if (!projectCategories) return [];
    const _projectCategories: TCategoryOption[] = [];
    projectCategories.forEach((option) => {
      _projectCategories.push({
        label: option,
        value: option,
        meta: option,
      });
    });
    return _projectCategories;
  }, [projectCategories]);

  const defaultProjectCategories = getDefaultProjectCategories();

  const handleSaveCategories = useCallback(
    (updatedCategories: TCategoryOption[]) => {
      // compare new with old and only continue if they are different
      const _defaultProjectCategories = getDefaultProjectCategories();
      if (_defaultProjectCategories.length === updatedCategories.length) {
        const defaultProjectCategoriesSet = new Set(_defaultProjectCategories.map((option) => option.value));
        const updatedCategoriesSet = new Set(updatedCategories.map((option) => option.value));
        if (Array.from(defaultProjectCategoriesSet).every((value) => updatedCategoriesSet.has(value))) {
          return;
        }
      }

      // update project categories
      const categoryValues = updatedCategories.map((option) => option.value);
      dispatch(saveWorkingProjectByFields({ categories: categoryValues }));
      // if syncSessionUuid is provided, update the session tracks as well
      if (syncSessionUuid) {
        dispatch(
          saveWorkingProjectSessionByField({
            uuid: syncSessionUuid,
            tracks: categoryValues,
          })
        );
      }

      // if new category is created, update redux state to include it
      // no neeed to udpate database, because it will be updated in the previous step directly on the project
      // and the "channel" list is just a unique list of all categories from every channel project
      const currentCategories = allProjectCategoriesForChannel || [];
      const channelCategoriesSet = new Set(currentCategories);
      const updatedCategoriesSet = new Set(updatedCategories.map((option) => option.value));
      const newCategories = Array.from(updatedCategoriesSet).filter((value) => !channelCategoriesSet.has(value));
      if (newCategories.length) {
        // alphabetically sort the categories
        const newChannelTemplateFilters = Array.from(new Set([...currentCategories, ...newCategories])).sort((a, b) =>
          a.localeCompare(b)
        );

        dispatch(setAllProjectCategoriesInStoreOnly(newChannelTemplateFilters));
      }
    },
    [allProjectCategoriesForChannel, dispatch, getDefaultProjectCategories, syncSessionUuid]
  );

  return (
    <Container className={`z-1 ${styles.wrapper}`}>
      <ProjectCategoriesSelect
        onMenuClose={handleSaveCategories}
        defaultCategories={defaultProjectCategories}
        disabled={!hasPermissions}
      />
    </Container>
  );
};

export default EditProjectCategory;
